// Shared floor-side / call-direction rules for Control commands.
//
// Originally defined inline in setupWizard/steps/StepActions.tsx as
// CONTROL_DOOR_SIDE and CONTROL_CALL_DIRECTION. The monitoring control
// panel needs the same maps to decide which actions to render as a
// floor-button strip (per-floor commands like EnergizeCarCall) versus a
// single button (door-agnostic / bank-wide commands like VIP). Pulling
// the maps into a shared util keeps both consumers in sync — a schema
// rename or a new Control variant only needs to be classified here once.
//
// Sourced from `flatbuffer_schemas/schemas/ln2/control.fbs` via the
// generated Control enum. Front-door entries cover car-call
// energize/secure/lock/unlock + front hall-call up/down energize and
// lock/unlock, on the main riser and (energize only) the auxiliary riser;
// rear-door entries are the symmetric rear siblings. A Control value in
// neither table is not floor-based at this layer. An explicit "any" entry
// is different: it IS floor-based, and acts on the floor as a whole rather
// than on one of its openings.

import { ActionFloorScope } from "../flatbuffers/generated/archive/action-floor-scope";
import { Control } from "../flatbuffers/generated/control/control";
import { BankFloorOpening } from "./bankFloorOpenings";

/** Which side of the doorway a Control enum value applies to. */
export type DoorSide = "front" | "rear" | "any";

/** Hall-call direction; "none" disables top/bottom floor exclusion. */
export type CallDirection = "up" | "down" | "none";

/**
 * Door-side lookup for Control enum values. "front" / "rear" gate the
 * action on the matching door flag (front_door or rear_door). "any"
 * means the action is door-agnostic and lands on every floor, whichever
 * openings it has. A member listed here with "any" is floor-based; a member
 * absent from the map also reads "any" from `controlDoorSide`, but is not.
 */
export const CONTROL_DOOR_SIDE = new Map<number, DoorSide>([
  [Control.EnergizeCarCall, "front"],
  [Control.EnergizeRearCarCall, "rear"],
  [Control.EnergizeUpHallCall, "front"],
  [Control.EnergizeUpRearHallCall, "rear"],
  [Control.EnergizeDownHallCall, "front"],
  [Control.EnergizeDownRearHallCall, "rear"],
  [Control.SecureCarCall, "front"],
  [Control.SecureRearCarCall, "rear"],
  [Control.CarCallLock, "front"],
  [Control.CarCallUnlock, "front"],
  [Control.RearCarCallLock, "rear"],
  [Control.RearCarCallUnlock, "rear"],
  [Control.UpHallCallLock, "front"],
  [Control.UpHallCallUnlock, "front"],
  [Control.UpRearHallCallLock, "rear"],
  [Control.UpRearHallCallUnlock, "rear"],
  [Control.DownHallCallLock, "front"],
  [Control.DownHallCallUnlock, "front"],
  [Control.DownRearHallCallLock, "rear"],
  [Control.DownRearHallCallUnlock, "rear"],
  [Control.EnergizeUpAuxHallCall, "front"],
  [Control.EnergizeUpAuxRearHallCall, "rear"],
  [Control.EnergizeDownAuxHallCall, "front"],
  [Control.EnergizeDownAuxRearHallCall, "rear"],
  // Takes a floor but no door side: MCE marks BOTH openings of the floor
  // shuttle-eligible, so "front" would hide every rear-only floor. "any" keeps
  // rear-only floors and both extremes, and makes the command floor-based.
  [Control.ShuttleFloor, "any"],
]);

/**
 * Hall-call direction for the floor-extremes rule: "up" calls cannot
 * originate from the top floor, "down" calls cannot originate from the
 * bottom floor. Car-call and door-control actions are "none" (allowed
 * on every floor with the matching door).
 */
export const CONTROL_CALL_DIRECTION = new Map<number, CallDirection>([
  [Control.EnergizeUpHallCall, "up"],
  [Control.EnergizeUpRearHallCall, "up"],
  [Control.UpHallCallLock, "up"],
  [Control.UpHallCallUnlock, "up"],
  [Control.UpRearHallCallLock, "up"],
  [Control.UpRearHallCallUnlock, "up"],
  [Control.EnergizeDownHallCall, "down"],
  [Control.EnergizeDownRearHallCall, "down"],
  [Control.DownHallCallLock, "down"],
  [Control.DownHallCallUnlock, "down"],
  [Control.DownRearHallCallLock, "down"],
  [Control.DownRearHallCallUnlock, "down"],
  [Control.EnergizeUpAuxHallCall, "up"],
  [Control.EnergizeUpAuxRearHallCall, "up"],
  [Control.EnergizeDownAuxHallCall, "down"],
  [Control.EnergizeDownAuxRearHallCall, "down"],
]);

/**
 * Returns the door side for a Control enum value, defaulting to "any"
 * for variants that aren't in CONTROL_DOOR_SIDE. Use this when deciding
 * which floor buttons to enable in a per-floor render.
 */
export function controlDoorSide(enumValue: number): DoorSide {
  return CONTROL_DOOR_SIDE.get(enumValue) ?? "any";
}

/**
 * Returns the call direction for a Control enum value, defaulting to
 * "none". Use this to skip the top/bottom floor button when the
 * direction would be physically meaningless (no up-call from the top,
 * no down-call from the bottom).
 */
export function controlCallDirection(enumValue: number): CallDirection {
  return CONTROL_CALL_DIRECTION.get(enumValue) ?? "none";
}

/**
 * Returns true when the given Control enum value should be rendered as
 * a per-floor button strip rather than a single button. A control is
 * "floor-based" when either table lists it — a door side (front, rear,
 * or an explicit "any" for ShuttleFloor, which acts on the whole floor)
 * or a direction (up/down). Position-less commands (VIP, CloseDoor, etc.)
 * are in neither table, return false and stay as single buttons.
 */
export function isFloorBasedControl(enumValue: number): boolean {
  return CONTROL_DOOR_SIDE.has(enumValue) || CONTROL_CALL_DIRECTION.has(enumValue);
}

/**
 * The MCE generic outputs a `Control.GenericOutput` position may name. VTS
 * sends `position - 1` as C1027's OutputId and rejects any position outside
 * this range.
 */
export const GENERIC_OUTPUT_FIRST = 1;
export const GENERIC_OUTPUT_LAST = 32;

/**
 * True when a Control's `position` is an output number rather than a floor —
 * `Control.GenericOutput` alone. Such a command never gets a floor strip or a
 * floor field: the operator names the output number instead.
 */
export function takesOutputNumber(enumValue: number | null | undefined): boolean {
  return enumValue === Control.GenericOutput;
}

/** Whether a value is a generic output number VTS accepts: an integer, 1-32. */
export function isValidOutputNumber(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= GENERIC_OUTPUT_FIRST &&
    value <= GENERIC_OUTPUT_LAST
  );
}

/**
 * Whether a floor-based control must be given at least one floor.
 *
 * For most floor-based controls an empty floor set saves position 0, the
 * schema's "unused" value. ShuttleFloor's position is VTS's 0-based floor, so 0
 * is itself a floor: an empty set would silently add or remove THAT floor. The
 * control panel likewise keeps it a floor command whatever the server says, and
 * blocks its buttons rather than send 0 when no floor can be picked.
 */
export function controlRequiresFloor(enumValue: number | null | undefined): boolean {
  return enumValue === Control.ShuttleFloor;
}

/**
 * Whether an action renders the floor-button strip or a single plain button.
 *
 * Precedence, strongest first:
 *
 * 1. `floorScope` — the server's verdict as an `Archive.ActionFloorScope`
 *    value, read off `keys.floors_based` for the
 *    adapter key that carries this command. This is the signal the operator
 *    actually configures: the Floor toggle in the wizard's LN1 Key Mappings.
 *    It is authoritative in BOTH directions, which is the point — Car To Lobby
 *    and Independent Service have it off and must render one button.
 * 2. `takesPosition` (`messages.position`) — promotion only, never demotion.
 *    It is per-message, so it covers adapter-only commands the generated
 *    `Control` enum never declares. Kept as a fallback for servers that predate
 *    `floorScope`, but it cannot be trusted in either direction on its own: on
 *    a real site it is true on 76 of 80 message rows, including both Car To
 *    Lobby and Independent Service, and it is a NOT NULL column with no
 *    default, so a message that was never populated reads `false` — demoting
 *    on it would strip the floor buttons off every hall call on a site whose
 *    import predates the column.
 * 3. `isFloorBasedControl` — the hand-maintained enum table, last resort.
 *
 * UNKNOWN is a real answer meaning the server could not tell — no adapter key
 * matched the command, or the adapter's `floors_based` column is degenerate —
 * and it falls through rather than demoting. Showing a floor strip that is not
 * needed is untidy; hiding one that is needed stops an operator recalling a car
 * with someone inside.
 *
 * Two controls are decided before any of the three is read, because their
 * `position` means one thing whatever the server says: `Control.GenericOutput`
 * is refused a strip (its position is an output number), and
 * `Control.ShuttleFloor` always gets one (its position is a floor, and the
 * plain button's 0 would change its lowest floor).
 */
export function actionTakesFloor(
  takesPosition: boolean | null | undefined,
  enumValue: number | undefined,
  floorScope?: number | null,
): boolean {
  // No resolvable command means nothing to dispatch per floor, whatever the
  // server says about the message. The guard lives here rather than at the call
  // site so the card list and the floor list cannot drift on it.
  if (enumValue === undefined) return false;

  // A GenericOutput position is the controller's output number. A floor strip
  // would send a floor as that number, so no server signal can make it right.
  if (takesOutputNumber(enumValue)) return false;
  // The converse: ShuttleFloor's position is always a floor, and a plain button
  // would send 0 — its lowest floor — so no server signal can take its strip away.
  if (controlRequiresFloor(enumValue)) return true;

  if (floorScope === ActionFloorScope.FLOOR) return true;
  if (floorScope === ActionFloorScope.NO_FLOOR) return false;

  if (takesPosition === true) return true;

  return isFloorBasedControl(enumValue);
}

/**
 * Resolve the set of (bank) floor positions an action should land on,
 * given the bank's per-floor opening map. Applies door-side filtering
 * (front vs rear) and the hall-call top/bottom exclusion. When the bank
 * has no preloaded floor data, returns every position so the seeder
 * degrades to its prior "all floors" behaviour rather than emitting zero
 * actions.
 *
 * For lift-scoped (units_based) entries the caller passes the per-lift
 * opening map via `liftDoors`; for bank-scoped (hall) entries `liftDoors`
 * is undefined and the bank-floor flags are used.
 *
 * Moved here from setupWizard/steps/StepActions.tsx, which re-exports it: the
 * scheduled-action floor picker renders exactly this eligibility as checkboxes,
 * and a picker that disagreed with the seeder about which floors a command can
 * reach would offer an operator a floor the command cannot act on.
 */
export function eligibleFloorPositions(
  enumValue: number,
  bankFloors: BankFloorOpening[],
  liftDoors?: Map<string, { front: boolean; rear: boolean }>,
): number[] {
  return eligibleFloorOpenings(enumValue, bankFloors, liftDoors).map((f) => f.position);
}

/**
 * The same eligibility, returning the FLOORS rather than their positions.
 *
 * Monitoring needs the records, not the numbers: it renders a button per floor
 * (labelled with the floor's name) and dispatches the live command by
 * `floor_index`, so a position list cannot be mapped back — on a bank whose
 * `position` column is degenerate every floor answers 0 and the mapping is
 * ambiguous. Identity travels on `id`, which is unique whatever the position
 * column contains.
 *
 * This is the one implementation of the door-side and extremes rules;
 * `eligibleFloorPositions` is a projection of it. The monitoring control panel
 * used to carry its own copy (`eligibleFloorsFor`), and that copy lacked the
 * degenerate-order guard below — so on a bank with an all-zero position column
 * every floor read as both the top and the bottom, every up/down hall-call floor
 * was excluded, and the card was dropped for having nothing to render. The
 * command was configured, visible in the wizard, and unreachable on the screen.
 */
export function eligibleFloorOpenings(
  enumValue: number,
  bankFloors: BankFloorOpening[],
  liftDoors?: Map<string, { front: boolean; rear: boolean }>,
): BankFloorOpening[] {
  if (bankFloors.length === 0) return [];
  const side = controlDoorSide(enumValue);
  const direction = controlCallDirection(enumValue);
  // Ranked by `order`, which is `position` on a healthy bank and the recovered
  // floor_no/floor_index on one whose position column is degenerate. The
  // EXTREMES are a question about height, so they read the rank; what the
  // function RETURNS is `position`, the only floor number the server matches.
  const ranks = bankFloors.map((f) => f.order);
  const topPos = Math.max(...ranks);
  const bottomPos = Math.min(...ranks);

  // Degenerate-order guard: >1 floor but every floor shares one position (the
  // order was never populated and could not be recovered upstream). We cannot
  // identify the physical top/bottom, so applying the extreme exclusion would
  // drop EVERY floor and silently omit the hall-call command. Skip the
  // exclusion and let door-side filtering decide — degrading to "all floors
  // eligible" rather than none. A genuine single-floor bank (length 1) still
  // applies the exclusion, correctly yielding zero eligible hall-call floors.
  const orderable = bankFloors.length < 2 || topPos !== bottomPos;

  return bankFloors.filter((floor) => {
    if (orderable && direction === "up" && floor.order === topPos) return false;
    if (orderable && direction === "down" && floor.order === bottomPos) return false;
    if (side === "any") return true;
    const doors = liftDoors?.get(floor.id) ?? { front: floor.front, rear: floor.rear };
    return side === "front" ? doors.front : doors.rear;
  });
}
